import React, { useEffect, useState } from 'react';
import { PhoneIcon, MailIcon, MenuIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WhatsAppIcon } from './WhatsAppIcon';
interface NavbarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}
export function Navbar({
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'hypotheken',
    label: 'Hypotheken'
  }, {
    id: 'woonboten',
    label: 'Woonboten'
  }, {
    id: 'verzekeringen',
    label: 'Verzekeringen'
  }, {
    id: 'over-ons',
    label: 'Over ons'
  }, {
    id: 'kosten',
    label: 'Kosten'
  }, {
    id: 'contact',
    label: 'Contact'
  }];
  const handleNavClick = (id: string) => {
    onNavigate(id);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };
  return <header className="fixed top-0 left-0 right-0 z-50 flex flex-col" data-id="element-108">
      {/* Top Bar */}
      <div className="bg-navy text-white py-2 px-4 md:px-8 text-xs md:text-sm font-medium" data-id="element-109">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-110">
          <div className="flex items-center space-x-6" data-id="element-111">
            <a href="tel:0232052161" className="flex items-center hover:text-gold transition-colors" data-id="element-112">
              <PhoneIcon className="w-3 h-3 md:w-4 md:h-4 mr-2" data-id="element-113" />
              023-205 2161
            </a>
            <a href="mailto:info@jdgadvies.nl" className="hidden md:flex items-center hover:text-gold transition-colors" data-id="element-114">
              <MailIcon className="w-4 h-4 mr-2" data-id="element-115" />
              info@jdgadvies.nl
            </a>
          </div>

        </div>
      </div>

      {/* Main Navigation */}
      <div className={`transition-all duration-300 ${isScrolled ? 'navbar-glass py-3' : 'bg-white py-4 md:py-5'}`} data-id="element-116">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center" data-id="element-117">
          {/* Logo + Descriptor */}
          <div className="cursor-pointer flex-shrink-0 flex items-center gap-2 sm:gap-3" onClick={() => handleNavClick('home')} data-id="element-118">
            <img src="https://cdn.magicpatterns.com/uploads/wwkRQsvFdLQeu28ZZ6FKqh/JDG_LOGO_NAV.png" alt="JDG Hypotheken | Verzekeringen" className="h-8 sm:h-8 md:h-14 object-contain" data-id="element-119" />
            <div className="border-l border-gray-200 pl-2 sm:pl-3" data-id="element-120">
              <span className="text-navy font-bold text-[10px] sm:text-xs md:text-sm leading-tight block" data-id="element-121">
                Hypotheken
              </span>
              <span className="text-gold font-bold text-[10px] sm:text-xs md:text-sm leading-tight block" data-id="element-122">
                Verzekeringen
              </span>
            </div>
          </div>

          {/* Desktop Links */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8" data-id="element-123">
            {navLinks.map(link => <button key={link.id} onClick={() => handleNavClick(link.id)} aria-current={currentPage === link.id ? 'page' : undefined} className={`nav-link text-sm xl:text-base ${currentPage === link.id ? 'active' : ''}`} data-id="element-124">
                {link.label}
              </button>)}
          </nav>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center space-x-4" data-id="element-125">
            <a href="tel:0232052161" className="hidden md:flex items-center bg-gold hover:bg-gold-dark text-white px-5 py-2.5 rounded-brand font-semibold transition-colors shadow-sm" data-id="element-126">
              <PhoneIcon className="w-4 h-4 mr-2" data-id="element-127" />
              023-205 2161
            </a>
            <button className="lg:hidden text-navy p-2" onClick={() => setIsMobileMenuOpen(true)} aria-label="Menu openen" data-id="element-128">
              <MenuIcon className="w-6 h-6" data-id="element-129" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-130">
        {isMobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-[60] lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-131" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-4/5 max-w-sm bg-white z-[70] shadow-2xl flex flex-col lg:hidden" data-id="element-132">
              <div className="p-6 flex justify-between items-center border-b border-gray-100" data-id="element-133">
                <img src="https://cdn.magicpatterns.com/uploads/wwkRQsvFdLQeu28ZZ6FKqh/JDG_LOGO_NAV.png" alt="JDG Logo" className="h-8 object-contain" data-id="element-134" />
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-500 hover:text-navy bg-gray-50 rounded-full" aria-label="Menu sluiten" data-id="element-135">
                  <XIcon className="w-5 h-5" data-id="element-136" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-6 px-6 flex flex-col space-y-6" data-id="element-137">
                {navLinks.map(link => <button key={link.id} onClick={() => handleNavClick(link.id)} aria-current={currentPage === link.id ? 'page' : undefined} className={`text-left text-lg font-medium py-2 border-b border-gray-50 ${currentPage === link.id ? 'text-gold' : 'text-navy'}`} data-id="element-138">
                    {link.label}
                  </button>)}
              </div>
              <div className="p-6 border-t border-gray-100 bg-warm" data-id="element-139">
                <p className="text-sm text-gray-500 mb-4" data-id="element-140">
                  Direct contact opnemen?
                </p>
                <a href="tel:0232052161" className="flex items-center justify-center w-full bg-navy text-white py-3 rounded-brand font-medium mb-3" data-id="element-141">
                  <PhoneIcon className="w-4 h-4 mr-2" data-id="element-142" />
                  Bel 023-205 2161
                </a>
                <a href="mailto:info@jdgadvies.nl" className="flex items-center justify-center w-full bg-white border border-gray-200 text-navy py-3 rounded-brand font-medium" data-id="element-143">
                  <MailIcon className="w-4 h-4 mr-2" data-id="element-144" />
                  Mail ons
                </a>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}